import { AnimatePresence, motion } from "motion/react"
import { type ReactNode } from "react"

import { Button } from "~/components/design-system/components"
import PageContent from "~/components/design-system/pages/internal/PageContent"
import {
  type SettingsPageProps,
  type SettingsRowProps,
  type SettingsSectionProps,
} from "~/components/design-system/pages/types"
import { UnsavedChangesGuard } from "~/components/planning/UnsavedChangesBar"
import { cn } from "~/utils/helpers"

/**
 * Archetype page — a settings form: title, description, then titled sections of setting rows.
 * Owns the save model: pass `form` and a sticky Discard / Save bar appears while the page is dirty,
 * and leaving the page with unsaved changes asks for confirmation.
 * Canonical intent: {@link ../intents.ts SETTINGS_PAGE_INTENT}.
 * Approved surface: design-system/APPROVED.md · Patterns: design-system/pages/README.md.
 */
const SettingsPage = ({
  title,
  description,
  actions,
  form,
  width = "md",
  children,
}: SettingsPageProps) => (
  <PageContent archetype="settings">
    <div
      className={cn(
        "mx-auto w-full flex flex-col gap-8 px-4 pt-8 pb-24",
        width === "md" ? "max-w-[760px]" : "max-w-[1080px]",
      )}
    >
      <header className="flex items-start justify-between gap-4">
        <div className="flex flex-col gap-1 min-w-0">
          <h1 className="heading-md text-fg-base">{title}</h1>
          {description && <p className="paragraph-base text-fg-subtle">{description}</p>}
        </div>
        {actions && <div className="flex shrink-0 items-center gap-2">{actions}</div>}
      </header>
      {children}
      {form && <SettingsSaveBar {...form} />}
    </div>
  </PageContent>
)

const SettingsSaveBar = ({
  isDirty,
  isSaving = false,
  onSave,
  onDiscard,
  requires = "administer",
  saveLabel = "Save changes",
}: NonNullable<SettingsPageProps["form"]>) => (
  <>
    <AnimatePresence>
      {isDirty && (
        <motion.div
          initial={{ opacity: 0, y: 16, scale: 0.98 }}
          animate={{ opacity: 1, y: 0, scale: 1 }}
          exit={{ opacity: 0, y: 16, scale: 0.98 }}
          transition={{ duration: 0.2, ease: "easeOut" }}
          className="sticky bottom-4 z-10 flex items-center justify-between gap-4 rounded-[12px] bg-base px-4 py-3 shadow-flyout"
        >
          <p className="label-base text-fg-base">You have unsaved changes</p>
          <div className="flex items-center gap-2">
            <Button variant="neutral" size="sm" onClick={onDiscard} disabled={isSaving}>
              Discard
            </Button>
            <Button size="sm" onClick={onSave} loading={isSaving} requires={requires}>
              {saveLabel}
            </Button>
          </div>
        </motion.div>
      )}
    </AnimatePresence>
    <UnsavedChangesGuard when={isDirty} />
  </>
)

/**
 * A titled group of settings rows inside `SettingsPage`.
 * Canonical intent: {@link ../intents.ts SETTINGS_SECTION_INTENT}.
 */
export const SettingsSection = ({
  title,
  description,
  actions,
  children,
}: SettingsSectionProps) => (
  <section className="flex flex-col gap-3">
    {(title || actions) && (
      <div className="flex items-end justify-between gap-4 px-1">
        <div className="flex flex-col gap-0.5 min-w-0">
          {title && <h2 className="heading-xs text-fg-base">{title}</h2>}
          {description && <p className="paragraph-sm text-fg-subtle">{description}</p>}
        </div>
        {actions && <div className="flex shrink-0 items-center gap-2">{actions}</div>}
      </div>
    )}
    <div className="@container flex flex-col rounded-[12px] bg-base shadow-card-rest divide-y divide-border-base">
      {children}
    </div>
  </section>
)

/**
 * One setting inside a `SettingsSection`: label + help text on the left, control on the right.
 * `layout="stacked"` puts wide content (tables, lists, editors) below the label instead.
 * Canonical intent: {@link ../intents.ts SETTINGS_ROW_INTENT}.
 */
export const SettingsRow = ({
  label,
  description,
  htmlFor,
  badge,
  leading,
  layout = "inline",
  density = "default",
  children,
}: SettingsRowProps) => {
  const heading: ReactNode = (label || description) && (
    <div className="flex items-center gap-3 min-w-0">
      {leading && <div className="shrink-0">{leading}</div>}
      <div className="flex flex-col gap-0.5 min-w-0">
        {label && (
          <div className="flex items-center gap-2">
            {htmlFor ? (
              <label htmlFor={htmlFor} className="label-base text-fg-base">
                {label}
              </label>
            ) : (
              <span className="label-base text-fg-base">{label}</span>
            )}
            {badge}
          </div>
        )}
        {description && (
          <div className="paragraph-sm text-fg-subtle break-words">{description}</div>
        )}
      </div>
    </div>
  )

  if (layout === "stacked") {
    return (
      <div className="flex flex-col gap-3 px-4 py-4">
        {heading}
        {children}
      </div>
    )
  }

  return (
    <div
      className={cn(
        "flex flex-col gap-3 px-4 @md:flex-row @md:items-center @md:justify-between @md:gap-6",
        density === "compact" ? "py-2" : "py-3.5",
      )}
    >
      {heading}
      <div className="flex shrink-0 items-center gap-2 @md:max-w-[50%] @md:justify-end">
        {children}
      </div>
    </div>
  )
}

export default SettingsPage
